import { z } from "zod";
import { IsoDateTimeSchema, LocaleSchema } from "../common/primitives.js";
const SAFE_PATH = /^\/(?![/\\])[A-Za-z0-9\-._~!$&'()*+,;=:@/%?]*$/;
const RETURN_TO_PREFIXES = [
  "/o/",
  "/s/",
  "/me/",
  "/onboarding",
  "/invitations/",
  "/spaces/"
];
const RETURN_TO_PATHS = ["/", "/spaces"];
function isSafeReturnTo(path) {
  if (path.length > 1e3 || !SAFE_PATH.test(path)) return false;
  const pathname = path.split(/[?#]/, 1)[0] ?? "";
  if (pathname.includes("/../") || pathname.endsWith("/..")) return false;
  return RETURN_TO_PATHS.includes(pathname) || RETURN_TO_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}
const PLATFORM_RETURN_TO_ROOTS = [
  "/organizations",
  "/break-glass",
  "/jobs",
  "/security",
  "/audit",
  "/plans",
  "/deletions",
  "/users"
];
function isSafePlatformReturnTo(path) {
  if (path.length > 1e3 || !SAFE_PATH.test(path)) return false;
  const pathname = path.split(/[?#]/, 1)[0] ?? "";
  if (pathname.includes("/../") || pathname.endsWith("/..")) return false;
  return pathname === "/" || PLATFORM_RETURN_TO_ROOTS.some((root) => pathname === root || pathname.startsWith(`${root}/`));
}
const PlatformReturnToSchema = z.string().max(1e3).refine(isSafePlatformReturnTo, { error: "returnTo must be a relative path of the back-office" }).default("/");
const ReturnToSchema = z.string().max(1e3).refine(isSafeReturnTo, { error: "returnTo must be a relative path of the app" }).default("/");
const LoginQuerySchema = z.strictObject({
  returnTo: ReturnToSchema,
  enrol: z.enum(["totp"]).optional(),
  /** Language of Keycloak's pages (ui_locales), so sign-in matches the app. */
  locale: LocaleSchema.optional(),
  /**
   * Re-authenticate now (max_age=0): after setting up an authenticator, so the new session carries
   * it (setting it up is not an authentication), or after setting one up in another tab.
   */
  fresh: z.enum(["1"]).optional()
});
const StepUpQuerySchema = z.strictObject({
  returnTo: ReturnToSchema,
  reauth: z.enum(["1"]).optional()
});
const CallbackQuerySchema = z.strictObject({
  code: z.string().min(1).max(4096).optional(),
  state: z.string().min(1).max(512).optional(),
  iss: z.string().max(512).optional(),
  session_state: z.string().max(512).optional(),
  error: z.string().max(128).optional(),
  error_description: z.string().max(1e3).optional(),
  error_uri: z.string().max(1e3).optional(),
  kc_action: z.string().max(64).optional(),
  kc_action_status: z.string().max(64).optional()
});
const LogoutResponseSchema = z.strictObject({
  endSessionUrl: z.url()
});
const BackchannelLogoutBodySchema = z.strictObject({
  logout_token: z.string().min(1).max(8192)
});
const SessionSummarySchema = z.strictObject({
  id: z.string().min(16).max(64),
  current: z.boolean(),
  createdAt: IsoDateTimeSchema,
  lastSeenAt: IsoDateTimeSchema,
  expiresAt: IsoDateTimeSchema,
  /** Short device description (browser and system), e.g. "Firefox · Windows". */
  device: z.string().max(100).nullable(),
  /** Network prefix only (IPv4 /24, IPv6 /48), never the full address. */
  ipPrefix: z.string().max(50).nullable(),
  /** The network or browser changed during the session: sensitive actions need a fresh step-up. */
  flagged: z.boolean()
});
const SessionListSchema = z.strictObject({ items: z.array(SessionSummarySchema) });
const SessionIdParamSchema = z.strictObject({ sessionId: z.string().min(16).max(64) });
const RevokeOthersResponseSchema = z.strictObject({ revoked: z.int().min(0) });
export {
  BackchannelLogoutBodySchema,
  CallbackQuerySchema,
  LoginQuerySchema,
  LogoutResponseSchema,
  PLATFORM_RETURN_TO_ROOTS,
  PlatformReturnToSchema,
  RETURN_TO_PATHS,
  RETURN_TO_PREFIXES,
  ReturnToSchema,
  RevokeOthersResponseSchema,
  SessionIdParamSchema,
  SessionListSchema,
  SessionSummarySchema,
  StepUpQuerySchema,
  isSafePlatformReturnTo,
  isSafeReturnTo
};
//# sourceMappingURL=index.js.map